import * as React from "react";
import axios from "axios";
import { useDispatch, useSelector } from "@teamprevent/admin-redux";

import { isRequestActive, loadAndTrack } from "redux-request-loading";

import { Questionnaire } from "@teamprevent/common/questionnaires/model";
import { HealthSurveillanceQuestionnaireKey } from "@teamprevent/common/healthSurveillance";
import { questionnaireLoaded } from "@teamprevent/common/questionnaires/actions";

import { ApplicationState } from "../../applicationState";

import { clinicHealthSurveillanceQuestionnaireLoaded } from "../actions";
import { HealthSurveillanceApi } from "../healthSurveillanceApi";
import { ClinicHealthSurveillanceQuestionnaire } from "../models";

type useHealthSurveillanceQuestionnaireReturn =
    { questionnaire: Questionnaire | null; isLoading: false; isError: false; refresh: () => void; isLoaded: true }
    | { questionnaire: undefined; isLoading: boolean; isError: boolean; refresh: () => void; isLoaded: false };

/*
* This hook is used to load the HS questionnaire linked to an appointment in a clinic.
* If you don't need the questionnaire, you can pass `enable: false` to the hook to disable the loading of the questionnaire
* until you need it by calling `refresh()`
*/
export const useHealthSurveillanceQuestionnaire = (
    { clinicId, appointmentId }: { clinicId: string; appointmentId: string },
    { enable }: { enable?: boolean } = {}
): useHealthSurveillanceQuestionnaireReturn => {
    const dispatch = useDispatch();
    const questionnairesForClinic = useSelector<
        ApplicationState,
        ClinicHealthSurveillanceQuestionnaire[]
    >(state => state.healthSurveillance.questionnaires.filter(q => q.clinicId === clinicId));
    const questionnaireForClinic = questionnairesForClinic.find(q => q.appointmentId === appointmentId);
    const questionnaire = questionnaireForClinic?.questionnaire;
    const requestKey = `LOAD_HEALTH_SURVEILLANCE_QUESTIONNAIRE_${clinicId}_${appointmentId}`;
    const isLoading = useSelector<ApplicationState, boolean>(state => isRequestActive(state, requestKey));
    const [isError, setIsError] = React.useState(false);
    const refresh = React.useCallback(async () => {
        try {
            // TODO: Replace this with Redux Toolkit
            // @ts-expect-error TS2345
            await loadAndTrack(dispatch, requestKey, (async () => {
                const result = await new HealthSurveillanceApi(axios).loadQuestionnaireByAppointmentId(clinicId, appointmentId);
                dispatch(clinicHealthSurveillanceQuestionnaireLoaded({
                    clinicId,
                    appointmentId,
                    questionnaire: result
                }));

                if (!result || !result.data) {
                    return;
                }

                dispatch(questionnaireLoaded({
                    questionnaireKey: HealthSurveillanceQuestionnaireKey,
                    questions: result.data.questions,
                    eTag: result.eTag
                }));
            })());
            setIsError(false);
        } catch (e) {
            setIsError(true);
            throw e;
        }
    }, [clinicId, dispatch, appointmentId, requestKey]);
    React.useEffect(() => {
        if (enable === false
            || isLoading
            || isError
            || questionnaire !== undefined) {
            return;
        }

        refresh();
    }, [enable, isError, isLoading, questionnaire, refresh]);

    if (questionnaire === undefined) {
        return { questionnaire: undefined, isLoading, isError, refresh, isLoaded: false };
    } else {
        return { questionnaire, isLoading: false, isError: false, refresh, isLoaded: true };
    }
};
